import { type ReactNode, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useChartStrings } from "./strings.ts";

/** One of the five chart mark tokens (`--chart-1…5`). */
export type ChartTone = 1 | 2 | 3 | 4 | 5;

/** The same figures as the chart: header cells, then one row of cells per category or day. */
export interface ChartTable {
  columns: readonly string[];
  rows: readonly (readonly string[])[];
}

const FILL: Record<ChartTone, string> = {
  1: "fill-chart-1",
  2: "fill-chart-2",
  3: "fill-chart-3",
  4: "fill-chart-4",
  5: "fill-chart-5",
};
const STROKE: Record<ChartTone, string> = {
  1: "stroke-chart-1",
  2: "stroke-chart-2",
  3: "stroke-chart-3",
  4: "stroke-chart-4",
  5: "stroke-chart-5",
};
export const fillClass = (tone: ChartTone) => FILL[tone];
export const strokeClass = (tone: ChartTone) => STROKE[tone];

/** The tone of the nth series when none is given: 1, 2, … 5, then around again. */
export const toneAt = (index: number): ChartTone => ((index % 5) + 1) as ChartTone;

/**
 * Pattern overlays, so series differ by texture as well as colour (never colour alone): the
 * first series is solid, then hatching, dots, cross-hatching and horizontal lines.
 */
const PATTERNS = ["", "hatch", "dots", "cross", "lines"] as const;
export type ChartPattern = (typeof PATTERNS)[number];
export const patternAt = (index: number): ChartPattern => PATTERNS[index % PATTERNS.length] ?? "";

/** `<defs>` for the pattern overlays of one chart; ids are prefixed by the chart's own id. */
export function PatternDefs({ id }: { id: string }) {
  const line = "stroke-surface-raised";
  return (
    <defs>
      <pattern id={`${id}-hatch`} width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M-1,1 l2,-2 M0,6 l6,-6 M5,7 l2,-2" className={line} strokeWidth="1.5" />
      </pattern>
      <pattern id={`${id}-dots`} width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="2.5" cy="2.5" r="1.1" className="fill-surface-raised" />
      </pattern>
      <pattern id={`${id}-cross`} width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0,0 l6,6 M6,0 l-6,6" className={line} strokeWidth="1.2" />
      </pattern>
      <pattern id={`${id}-lines`} width="6" height="4" patternUnits="userSpaceOnUse">
        <path d="M0,2 h6" className={line} strokeWidth="1.2" />
      </pattern>
    </defs>
  );
}

/** A filled rect plus its pattern overlay. */
export function PatternedRect({
  chartId,
  tone,
  pattern,
  ...rect
}: { chartId: string; tone: ChartTone; pattern: ChartPattern } & {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  return (
    <g>
      <rect {...rect} className={fillClass(tone)} />
      {pattern ? <rect {...rect} fill={`url(#${chartId}-${pattern})`} /> : null}
    </g>
  );
}

/** A legend swatch drawing the same colour and pattern as the marks it names. */
export function Swatch({
  tone,
  pattern,
  dash,
}: {
  tone: ChartTone;
  pattern?: ChartPattern;
  /** Line charts: the series' dash pattern instead of a filled square. */
  dash?: string;
}) {
  const id = useId().replace(/:/g, "");
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" className="shrink-0">
      {dash === undefined ? (
        <>
          <PatternDefs id={id} />
          <PatternedRect
            chartId={id}
            tone={tone}
            pattern={pattern ?? ""}
            x={0}
            y={0}
            width={14}
            height={14}
          />
        </>
      ) : (
        <path d="M0,7 h14" className={strokeClass(tone)} strokeWidth="2.5" strokeDasharray={dash} />
      )}
    </svg>
  );
}

/**
 * Shared shell of every chart: a figure named by its title, the summary sentence as the chart's
 * description (announced by screen readers), and a keyboard-reachable toggle that swaps the
 * graphic for a table of the same figures.
 */
export function ChartFrame({
  title,
  summary,
  table,
  className,
  children,
}: {
  title: string;
  summary: string;
  table: ChartTable;
  className?: string;
  /** Receives the ids to put on the graphic: `aria-labelledby` and `aria-describedby`. */
  children: (ids: { titleId: string; summaryId: string }) => ReactNode;
}) {
  const strings = useChartStrings();
  const [asTable, setAsTable] = useState(false);
  const base = useId();
  const titleId = `${base}-title`;
  const summaryId = `${base}-summary`;
  return (
    <figure aria-labelledby={titleId} className={cn("flex min-w-0 flex-col gap-3", className)}>
      <figcaption className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0 flex-1">
          <div id={titleId} className="text-sm font-medium text-fg">
            {title}
          </div>
          <p id={summaryId} className="text-sm text-fg-secondary">
            {summary}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => setAsTable((v) => !v)}
          className="text-fg-secondary"
        >
          {asTable ? strings.showChart : strings.showTable}
        </Button>
      </figcaption>
      {asTable ? (
        <Table aria-labelledby={titleId} aria-describedby={summaryId}>
          <TableHeader>
            <TableRow>
              {table.columns.map((column, i) => (
                <TableHead key={column} className={i > 0 ? "text-right" : undefined}>
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow key={row.join("\u0000")}>
                {row.map((cell, i) =>
                  i === 0 ? (
                    <TableHead key={table.columns[i]} scope="row" className="font-normal">
                      {cell}
                    </TableHead>
                  ) : (
                    <TableCell key={table.columns[i]} className="text-right tabular-nums">
                      {cell}
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        children({ titleId, summaryId })
      )}
    </figure>
  );
}

/** The `useId` of a chart, usable inside SVG `url(#…)` references. */
export function useChartId(): string {
  return `chart${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}
